import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { formatINR, presetRange, todayStr, gradeClasses } from "@/lib/format";
import { SectionTitle } from "@/components/Shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Trophy, ArrowUp, ArrowDown, Minus, Download, Users2, ChevronDown } from "lucide-react";
import { ResponsiveContainer, LineChart, Line } from "recharts";

const PRESETS = [
  { id: "this_week", label: "This week" }, { id: "last_7", label: "Last 7 days" },
  { id: "this_month", label: "This month" }, { id: "last_month", label: "Last month" },
];
const COMPONENT_LABELS = { revenue: "Revenue", visits: "Visits", calls: "Calls", connect_rate: "Connect", treatment_conversion: "Treatment" };

function RankChange({ val }) {
  if (val > 0) return <span className="inline-flex items-center gap-0.5 text-emerald-400 text-xs font-bold"><ArrowUp className="h-3 w-3" />{val}</span>;
  if (val < 0) return <span className="inline-flex items-center gap-0.5 text-rose-400 text-xs font-bold"><ArrowDown className="h-3 w-3" />{Math.abs(val)}</span>;
  return <span className="inline-flex items-center text-slate-500 text-xs font-bold"><Minus className="h-3 w-3" /></span>;
}

function exportCSV(rows, filename) {
  if (!rows.length) return;
  const cols = Object.keys(rows[0]);
  const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => r[c]).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function Leaderboard() {
  const { telecallers } = useApp();
  const [range, setRange] = useState(presetRange("this_month"));
  const [selected, setSelected] = useState([]);
  const [data, setData] = useState(null);

  useEffect(() => {
    const ids = selected.length ? selected.join(",") : "all";
    api.scoring({ start: range.start, end: range.end, employee_ids: ids }).then(setData);
  }, [range, selected]);

  const board = data?.leaderboard || [];
  const podium = board.slice(0, 3);
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const exportRows = board.map((p) => ({
    rank: p.rank, name: p.name, score: p.score, grade: p.grade,
    revenue: p.metrics?.revenue, visits: p.metrics?.total_visits, calls: p.metrics?.dialed,
    connect_rate: p.metrics?.connect_rate, treat_conv: p.metrics?.treat_conv,
  }));

  return (
    <div className="animate-fade-up">
      <SectionTitle icon={Trophy} title="Scoring Leaderboard" desc="Ranked team performance out of 100"
        right={<Button variant="outline" size="sm" className="rounded-lg" data-testid="export-scoring-btn" onClick={() => exportCSV(exportRows, `scoring-${range.start}.csv`)}><Download className="h-4 w-4 mr-1.5" /> Export</Button>} />

      <div className="surface p-4 mb-6 flex flex-wrap items-end gap-3">
        <div className="flex flex-wrap gap-1.5">
          {PRESETS.map((p) => (
            <button key={p.id} data-testid={`score-preset-${p.id}`} onClick={() => setRange(presetRange(p.id))}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white/5 text-slate-300 hover:bg-[#C9A24B]/15 hover:text-[#C9A24B] transition-colors">{p.label}</button>
          ))}
        </div>
        <div className="flex items-end gap-2">
          <div><label className="text-[11px] text-slate-500 font-semibold">From</label><Input type="date" value={range.start} max={todayStr()} onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))} className="h-9 rounded-lg w-36" data-testid="score-start" /></div>
          <div><label className="text-[11px] text-slate-500 font-semibold">To</label><Input type="date" value={range.end} max={todayStr()} onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))} className="h-9 rounded-lg w-36" data-testid="score-end" /></div>
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="h-9 rounded-lg" data-testid="score-employee-multi">
              <Users2 className="h-4 w-4 mr-1.5" /> {selected.length ? `${selected.length} selected` : "All employees"} <ChevronDown className="h-4 w-4 ml-1.5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-56">
            <div className="space-y-1.5 max-h-64 overflow-y-auto scrollbar-thin">
              <button className="text-xs text-[#C9A24B] font-semibold mb-1" onClick={() => setSelected([])}>Select all</button>
              {telecallers.map((e) => (
                <label key={e.id} className="flex items-center gap-2 text-sm cursor-pointer py-1" data-testid={`score-emp-${e.id}`}>
                  <Checkbox checked={selected.includes(e.id)} onCheckedChange={() => toggle(e.id)} /> {e.name}
                </label>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {data?.weights && (
        <div className="text-xs text-slate-500 mb-4 flex flex-wrap gap-2 items-center">
          <span className="font-semibold text-slate-400">Weights:</span>
          {Object.entries(data.weights).map(([k, v]) => <span key={k} className="bg-white/5 px-2 py-0.5 rounded-md">{COMPONENT_LABELS[k]} {v}%</span>)}
        </div>
      )}

      {podium.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          {podium.map((p, i) => {
            const medal = ["from-[#C9A24B] to-[#a07d33]", "from-slate-400 to-slate-500", "from-orange-500 to-amber-700"][i];
            return (
              <div key={p.employee_id} className={`rounded-2xl p-5 text-white bg-gradient-to-br ${medal} ${i === 0 ? "sm:-translate-y-2" : ""} shadow-lg`} data-testid={`podium-${i + 1}`}>
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-heading font-light">#{p.rank}</span>
                  <span className="text-xs font-bold bg-black/25 px-2 py-1 rounded-md">Grade {p.grade}</span>
                </div>
                <div className="font-heading font-semibold text-lg mt-2">{p.name}</div>
                <div className="font-mono font-extrabold text-4xl mt-1">{p.score}</div>
                <div className="text-xs text-white/80 mt-1 font-mono">{formatINR(p.metrics?.revenue || 0)}</div>
              </div>
            );
          })}
        </div>
      )}

      <div className="surface p-5">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm" data-testid="leaderboard-table">
            <thead>
              <tr className="text-left text-slate-500 text-xs uppercase tracking-wide border-b border-white/10">
                <th className="py-2 pr-3">#</th><th className="py-2 px-3">Name</th><th className="py-2 px-3 text-center">Δ</th>
                <th className="py-2 px-3 text-right">Score</th><th className="py-2 px-3 text-center">Grade</th>
                {Object.keys(COMPONENT_LABELS).map((k) => <th key={k} className="py-2 px-3 text-right hidden md:table-cell">{COMPONENT_LABELS[k]}</th>)}
                <th className="py-2 pl-3 text-right">Trend</th>
              </tr>
            </thead>
            <tbody>
              {board.length === 0 && <tr><td colSpan={11} className="py-8 text-center text-slate-500">No data for this selection</td></tr>}
              {board.map((p) => (
                <tr key={p.employee_id} className="border-b border-white/5 hover:bg-white/5" data-testid={`leaderboard-row-${p.employee_id}`}>
                  <td className="py-3 pr-3 font-mono font-bold text-slate-500">{p.rank}</td>
                  <td className="py-3 px-3 font-semibold text-slate-100">{p.name}</td>
                  <td className="py-3 px-3 text-center"><RankChange val={p.rank_change} /></td>
                  <td className="py-3 px-3 text-right font-mono font-bold text-lg text-white">{p.score}</td>
                  <td className="py-3 px-3 text-center"><span className={`text-xs font-bold px-2 py-0.5 rounded-md border ${gradeClasses[p.grade]}`}>{p.grade}</span></td>
                  {Object.keys(COMPONENT_LABELS).map((k) => <td key={k} className="py-3 px-3 text-right font-mono text-slate-400 hidden md:table-cell">{p.components[k]}</td>)}
                  <td className="py-3 pl-3">
                    <div className="w-20 h-8 ml-auto">
                      {(p.trend || []).length > 1 ? (
                        <ResponsiveContainer width="100%" height="100%"><LineChart data={p.trend}><Line type="monotone" dataKey="revenue" stroke="#C9A24B" strokeWidth={2} dot={false} /></LineChart></ResponsiveContainer>
                      ) : <span className="text-xs text-slate-600">—</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
